import React, { useState } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { Reply, MoreHorizontal, Check, X, Smile } from 'lucide-react';
import { CommentComposer } from './CommentComposer';
import { useUpdateComment, useDeleteComment, useAddEmojiReaction, useRemoveEmojiReaction } from '@/hooks/useKBData';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Comment } from '@/types/api';

interface CommentTextProps {
  body: string;
}

function CommentText({ body }: CommentTextProps) {
  // Parse the text and convert @ mentions to pill components
  // Simple approach: split on words starting with @ and containing typical name characters
  const mentionRegex = /@[A-Za-z][A-Za-z0-9\s]*(?=[^A-Za-z0-9\s]|$)/g;

  // Replace mentions with placeholder tokens first
  const tokens: Array<{ type: 'text' | 'mention'; content: string }> = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(body)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      tokens.push({
        type: 'text',
        content: body.slice(lastIndex, match.index)
      });
    }

    // Add the mention
    tokens.push({
      type: 'mention',
      content: match[0]
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < body.length) {
    tokens.push({
      type: 'text',
      content: body.slice(lastIndex)
    });
  }

  return (
    <>
      {tokens.map((token, index) => {
        if (token.type === 'mention') {
          const mentionName = token.content.slice(1).trim(); // Remove @ and trim
          return (
            <span
              key={index}
              className="inline-flex items-center px-2 py-0.5 mx-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
            >
              @{mentionName}
            </span>
          );
        }

        return <span key={index}>{token.content}</span>;
      })}
    </>
  );
}

interface CommentItemProps {
  comment: Comment;
  articleSlug: string;
  level?: number;
}

export function CommentItem({ comment, articleSlug, level = 0 }: CommentItemProps) {
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editBody, setEditBody] = useState(comment.body);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const updateComment = useUpdateComment();
  const deleteComment = useDeleteComment();
  const addEmojiReaction = useAddEmojiReaction();
  const removeEmojiReaction = useRemoveEmojiReaction();

  // Get reactions from comment data (from backend) or default to empty
  const reactions = comment.reactions || {};

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleSaveEdit = () => {
    if (editBody.trim() === comment.body) {
      setIsEditing(false);
      return;
    }

    updateComment.mutate({
      commentId: comment.id,
      data: { body: editBody.trim() },
      articleSlug,
    }, {
      onSuccess: () => {
        setIsEditing(false);
      },
    });
  };

  const handleCancelEdit = () => {
    setEditBody(comment.body);
    setIsEditing(false);
  };

  const handleResolve = () => {
    updateComment.mutate({
      commentId: comment.id,
      data: { status: comment.status === 'open' ? 'resolved' : 'open' },
      articleSlug,
    });
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      deleteComment.mutate({
        commentId: comment.id,
        articleSlug,
      });
    }
  };

  const handleEmojiReaction = (emoji: string) => {
    // Check if user already reacted with this emoji
    // For now, we'll always add the reaction (could improve with user reaction tracking)
    addEmojiReaction.mutate({
      commentId: comment.id,
      emoji,
      articleSlug
    }, {
      onSuccess: () => {
        console.log('✅ Emoji reaction added successfully');
      },
      onError: (error) => {
        console.error('❌ Failed to add emoji reaction:', error);
      }
    });
    setShowEmojiPicker(false);
  };

  const isResolved = comment.status === 'resolved';
  const hasReplies = comment.replies && comment.replies.length > 0;

  return (
    <div className={`${level > 0 ? 'ml-8 border-l border-border pl-4' : ''}`}>
      <div className={`flex gap-3 ${isResolved ? 'opacity-60' : ''}`}>
        <Avatar className="h-8 w-8">
          <AvatarFallback className="text-xs bg-primary/10 text-primary">
            {getUserInitials(comment.author_name)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm">{comment.author_name}</span>
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(comment.created_at + 'Z'), { addSuffix: true })}
            </span>
            {isResolved && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                Resolved
              </span>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-3">
              <textarea
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                className="w-full min-h-[60px] p-2 text-sm border border-border rounded resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                autoFocus
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleSaveEdit}
                  disabled={updateComment.isPending || !editBody.trim()}
                >
                  <Check className="h-3 w-3 mr-1" />
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelEdit}
                >
                  <X className="h-3 w-3 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm whitespace-pre-wrap">
                <CommentText body={comment.body} />
              </div>

              {/* Emoji reactions display */}
              {Object.entries(reactions).some(([_, count]) => count > 0) && (
                <div className="flex items-center gap-1 mb-2">
                  {Object.entries(reactions)
                    .filter(([_, count]) => count > 0)
                    .map(([emoji, count]) => (
                      <button
                        key={emoji}
                        onClick={() => handleEmojiReaction(emoji)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-xs"
                      >
                        <span>{emoji}</span>
                        <span>{count}</span>
                      </button>
                    ))
                  }
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsReplying(!isReplying)}
                  className="h-6 px-2 text-xs"
                >
                  <Reply className="h-3 w-3 mr-1" />
                  Reply
                </Button>

                <div className="relative">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="h-6 px-2 text-xs"
                  >
                    <Smile className="h-3 w-3" />
                  </Button>

                  {showEmojiPicker && (
                    <div className="absolute top-8 left-0 z-50 bg-white border border-border rounded-lg shadow-lg p-2 flex gap-1">
                      {['👍', '❤️', '😄', '🎉', '🚀', '👎'].map((emoji) => (
                        <button
                          key={emoji}
                          onClick={() => handleEmojiReaction(emoji)}
                          className="p-1 hover:bg-gray-100 rounded text-lg transition-colors"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                    >
                      <MoreHorizontal className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => setIsEditing(true)}>
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleResolve}>
                      {isResolved ? 'Reopen' : 'Mark as resolved'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleDelete}
                      className="text-red-600 focus:text-red-600"
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}

          {isReplying && (
            <div className="mt-3">
              <CommentComposer
                articleSlug={articleSlug}
                parentCommentId={comment.id}
                onCancel={() => setIsReplying(false)}
                onSuccess={() => setIsReplying(false)}
                placeholder={`Reply to ${comment.author_name}...`}
                compact
              />
            </div>
          )}
        </div>
      </div>

      {/* Nested replies */}
      {hasReplies && (
        <div className="mt-4 space-y-4">
          {comment.replies!.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              articleSlug={articleSlug}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}